import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import '../styles/Reports.css';
// Added TimeInput and ContactList so criteria inputs match CustomField rendering
import { Boolean, DateInput, Dropdown, MultiSelect, NumberInput, SearchSelect, Text, Contact, TimeInput, ContactList } from './FieldComponents';
import { Minus, PlusIcon, X, Plus, ChevronDown, Loader2, Info } from 'lucide-react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { isReportDateCriteriaField, singleReportDateCriteriaValue } from '../utils/reportDateCriteria';

const ItemTypes = {
    FIELD: 'field',
    CONDITION: 'condition',
};

const fieldIdsMatch = (left, right) => String(left) === String(right);

const contactNameTextFieldIds = new Set([
    'cases.contact_id',
    'leads.contact_id',
    'leads.referral_contact',
    'leads.db_origination',
    'contacts.id'
]);
const isContactNameTextOperator = (operator) => ['LIKE', 'NOT LIKE'].includes(String(operator || '').toUpperCase());
const isContactNameCriteriaField = (field) => {
    const fieldId = String(field?.id || '');
    const isCustomContactField = fieldId !== '' && !fieldId.includes('.') && [8, 9].includes(Number(field?.field_id));
    return isCustomContactField || contactNameTextFieldIds.has(fieldId);
};
const isTeamMemberCriteriaField = (field) => /^cases\.team\[['"]?\d+['"]?\]\[0\]$/.test(String(field?.id || ''));

const DraggableField = ({ field, addCondition }) => {
    const [{ isDragging }, drag] = useDrag(() => ({
        type: ItemTypes.FIELD,
        item: { field },
        collect: (monitor) => ({
            isDragging: !!monitor.isDragging(),
        }),
    }));

    return (
        <span
            ref={drag}
            className={`report-field ${isDragging ? 'dragging' : ''}`}
            onClick={() => addCondition(field.id)}
            style={{ opacity: isDragging ? 0.5 : 1, cursor: 'move' }}
        >
            {field.name}
        </span>
    );
};

export const ConditionRow = ({ uid, field_id, operator, value, deadline_part, onRemove, index, parentIndex, groupIndex, groupUid, moveCondition, customFields, updateCondition, phases, templates = [], users = [], caseTypes = [], marketingSources = [] }) => {
    // Temp holders for adding one-at-a-time in multi modes
    const [multiCaseTypeTemp, setMultiCaseTypeTemp] = useState('');
    const [multiPhaseTemp, setMultiPhaseTemp] = useState('');
    const [multiMarketingSourceTemp, setMultiMarketingSourceTemp] = useState('');
    const [contactResetKey, setContactResetKey] = useState(0);
    
    // Reset temp states when switching between single/multi modes
    useEffect(() => {
        setMultiCaseTypeTemp('');
        setMultiPhaseTemp('');
        setMultiMarketingSourceTemp('');
        setContactResetKey(prev => prev + 1); // Force Contact component reset
    }, [Array.isArray(value)]);

    // Auto-correct deadline_part for field_id 20 based on operator
    useEffect(() => {
        const field = customFields.find(f => fieldIdsMatch(f.id, field_id));
        if (field?.field_id === 20) {
            const isEquality = ['=','!='].includes(operator);
            // Force part to status when using equality operators
            if (isEquality && deadline_part !== 'status') {
                updateCondition(uid,'deadline_part','status');
            }
            // Auto-correct deadline_part if it's 'status' for non-equality operators
            if (!isEquality && deadline_part === 'status') {
                updateCondition(uid,'deadline_part','due');
            }
        }
    }, [field_id, operator, deadline_part, uid, customFields, updateCondition]);

    const rowRef = useRef(null);

    const [{ isDragging }, drag] = useDrag(() => ({
        type: ItemTypes.CONDITION,
        item: { 
            uid,
            index, 
            type: 'row',
            parentIndex,
            groupIndex,
            groupUid
        },
        collect: (monitor) => ({
            isDragging: !!monitor.isDragging(),
        }),
    }), [uid, index, parentIndex, groupIndex, groupUid]);

    const [, drop] = useDrop(() => ({
        accept: ItemTypes.CONDITION,
        hover: (item, monitor) => {
            if (item.index === index && item.parentIndex === parentIndex && item.groupIndex === groupIndex) return;
            if (!rowRef.current) return;
            if (item.parentIndex !== parentIndex || item.groupIndex !== groupIndex) return;

            const hoverBoundingRect = rowRef.current.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            if (!clientOffset) return;
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            if (item.index < index && hoverClientY < hoverMiddleY) return;
            if (item.index > index && hoverClientY > hoverMiddleY) return;

            moveCondition(item.index, index);
            item.index = index;
        },
    }), [index, parentIndex, groupIndex, moveCondition]);

    // (removed unused generalFieldConfig)

    const renderCriteriaInput = (field, value, onChange, operator, deadline_part) => {
        // Leads Deleted field - Yes/No dropdown (1 = Yes, 0/null = No)
        if (field?.id === 'leads.deleted') {
            const options = [
                [1, 'Yes'],
                [0, 'No']
            ];
            return <Dropdown options={JSON.stringify(options)} value={value} onChange={onChange} />;
        }

        // Case Type (single or multi OR logic when value is array)
        if (field?.id === 'leads.case_type_id' || field?.name === 'Case Type') {
            const options = caseTypes.map(ct => [Number(ct.id), ct.name]);
            const isMulti = Array.isArray(value);
            const currentIds = isMulti ? value : (value ? [value] : []);
            const toggleMulti = () => {
                if (isMulti) {
                    const first = currentIds[0] ?? null;
                    onChange(first);
                } else {
                    onChange(currentIds); // seed with existing single if present
                }
            };
            const removeId = (id) => {
                if (!isMulti) return;
                onChange(currentIds.filter(v => String(v) !== String(id)));
            };
            const addCaseType = (val) => {
                const numeric = val === '' ? null : Number(val);
                if (numeric == null || isNaN(numeric)) { setMultiCaseTypeTemp(''); return; }
                if (!currentIds.some(v => Number(v) === numeric)) {
                    onChange(isMulti ? [...currentIds, numeric] : numeric);
                }
                setMultiCaseTypeTemp('');
            };
            return (
                <div className="user-select-wrapper">
                    <div className='select-wrapped' style={{ display: 'flex', gap: "5px", alignItems: 'center', justifyContent: "flex-end", flexDirection: 'row-reverse' }}>
                        <div className="user-select-toggle-row">
                            <button type="button" className={`action alt ${isMulti ? 'active' : ''}`} onClick={toggleMulti} title="Toggle multi Case Type OR filter">{isMulti ? 'Multi' : 'Single'}</button>
                        </div>
                        {isMulti ? (
                            <div className='filter-group' style={{ width: '100%' }}>
                                <div className='select-wrapper' style={{ width: '100%' }}>
                                    <select className='filter-select' style={{ width: '100%' }} value={multiCaseTypeTemp} onChange={e => addCaseType(e.target.value)}>
                                        <option value=''>Add Case Type...</option>
                                        {options.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
                                    </select>
                                    <ChevronDown size={16} className="select-chevron" />
                                </div>
                            </div>
                        ) : (
                            <Dropdown options={JSON.stringify(options)} value={value} onChange={onChange} />
                        )}
                    </div>
                    {isMulti && currentIds.length > 0 && (
                        <div className="selected-users-chips">
                            {currentIds.map(id => {
                                const ct = caseTypes.find(c => Number(c.id) === Number(id));
                                return (
                                    <span key={id} className="chip">
                                        {(ct && ct.name) || id}
                                        <button type="button" className="chip-x" onClick={() => removeId(id)} aria-label="Remove case type">×</button>
                                    </span>
                                );
                            })}
                        </div>
                    )}
                </div>
            );
        }

        // Case Phase (supports multi as array for OR)
        if (field?.id === 'cases.phase_id' || field?.name === 'Case Phase') {
            const phaseOptions = phases.map(p => [Number(p.id), p.phase]);
            const isMulti = Array.isArray(value);
            const currentIds = isMulti ? value : (value ? [value] : []);
            const toggleMulti = () => {
                if (isMulti) {
                    const first = currentIds[0] ?? null;
                    onChange(first);
                } else {
                    onChange(currentIds);
                }
            };
            const removeId = (id) => {
                if (!isMulti) return;
                onChange(currentIds.filter(v => String(v) !== String(id)));
            };
            const addPhase = (val) => {
                const numeric = val === '' ? null : Number(val);
                if (numeric == null || isNaN(numeric)) { setMultiPhaseTemp(''); return; }
                if (!currentIds.some(v => Number(v) === numeric)) {
                    onChange(isMulti ? [...currentIds, numeric] : numeric);
                }
                setMultiPhaseTemp('');
            };
            return (
                <div className="user-select-wrapper">
                    <div className='select-wrapped' style={{ display: 'flex', gap: "5px", alignItems: 'center', justifyContent: "flex-end", flexDirection: 'row-reverse' }}>
                        <div className="user-select-toggle-row">
                            <button type="button" className={`action alt ${isMulti ? 'active' : ''}`} onClick={toggleMulti} title="Toggle multi Phase OR filter">{isMulti ? 'Multi' : 'Single'}</button>
                        </div>
                        {isMulti ? (
                            <div className='filter-group' style={{ width: '100%' }}>
                                <div className='select-wrapper' style={{ width: '100%' }}>
                                    <select className='filter-select' style={{ width: '100%' }} value={multiPhaseTemp} onChange={e => addPhase(e.target.value)}>
                                        <option value=''>Add Phase...</option>
                                        {phaseOptions.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
                                    </select>
                                    <ChevronDown size={16} className="select-chevron" />
                                </div>
                            </div>
                        ) : (
                            <Dropdown options={JSON.stringify(phaseOptions)} value={value} onChange={onChange} />
                        )}
                    </div>
                    {isMulti && currentIds.length > 0 && (
                        <div className="selected-users-chips">
                            {currentIds.map(id => {
                                const ph = phases.find(p => Number(p.id) === Number(id));
                                return (
                                    <span key={id} className="chip">
                                        {(ph && ph.phase) || id}
                                        <button type="button" className="chip-x" onClick={() => removeId(id)} aria-label="Remove phase">×</button>
                                    </span>
                                );
                            })}
                        </div>
                    )}
                </div>
            );
        }

        // Marketing Source (single or multi OR logic when value is array)
        if (field?.id === 'leads.marketing_source' || field?.name === 'Marketing Source') {
            const options = marketingSources?.filter(ms => [1,3].includes(Number(ms.marketing_type))).map(ms => [Number(ms.id), ms.source_name]);
            const isMulti = Array.isArray(value);
            const currentIds = isMulti ? value : (value ? [value] : []);
            const toggleMulti = () => {
                if (isMulti) {
                    const first = currentIds[0] ?? null;
                    onChange(first);
                } else {
                    onChange(currentIds);
                }
            };
            const removeId = (id) => {
                if (!isMulti) return;
                onChange(currentIds.filter(v => String(v) !== String(id)));
            };
            const addMarketingSource = (val) => {
                const numeric = val === '' ? null : Number(val);
                if (numeric == null || isNaN(numeric)) { setMultiMarketingSourceTemp(''); return; }
                if (!currentIds.some(v => Number(v) === numeric)) {
                    onChange(isMulti ? [...currentIds, numeric] : numeric);
                }
                setMultiMarketingSourceTemp('');
            };
            return (
                <div className="user-select-wrapper">
                    <div className='select-wrapped' style={{ display: 'flex', gap: "5px", alignItems: 'center', justifyContent: "flex-end", flexDirection: 'row-reverse' }}>
                        <div className="user-select-toggle-row">
                            <button type="button" className={`action alt ${isMulti ? 'active' : ''}`} onClick={toggleMulti} title="Toggle multi Marketing Source OR filter">{isMulti ? 'Multi' : 'Single'}</button>
                        </div>
                        {isMulti ? (
                            <div className='filter-group' style={{ width: '100%' }}>
                                <div className='select-wrapper' style={{ width: '100%' }}>
                                    <select className='filter-select' style={{ width: '100%' }} value={multiMarketingSourceTemp} onChange={e => addMarketingSource(e.target.value)}>
                                        <option value=''>Add Marketing Source...</option>
                                        {options.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
                                    </select>
                                    <ChevronDown size={16} className="select-chevron" />
                                </div>
                            </div>
                        ) : (
                            <Dropdown options={JSON.stringify(options)} value={value} onChange={onChange} />
                        )}
                    </div>
                    {isMulti && currentIds.length > 0 && (
                        <div className="selected-users-chips">
                            {currentIds.map(id => {
                                const ms = marketingSources.find(m => Number(m.id) === Number(id));
                                return (
                                    <span key={id} className="chip">
                                        {(ms && ms.source_name) || id}
                                        <button type="button" className="chip-x" onClick={() => removeId(id)} aria-label="Remove marketing source">×</button>
                                    </span>
                                );
                            })}
                        </div>
                    )}
                </div>
            );
        }

        if (field?.id === 'leads.marketing_lead' || field?.name === 'Marketing Lead') {
            const options = marketingSources?.filter(ms => ms.marketing_type == 2).map(ms => [Number(ms.id), ms.source_name]);
            const isMulti = Array.isArray(value);
            const currentIds = isMulti ? value : (value ? [value] : []);
            const toggleMulti = () => {
                if (isMulti) {
                    const first = currentIds[0] ?? null;
                    onChange(first);
                } else {
                    onChange(currentIds);
                }
            };
            const removeId = (id) => {
                if (!isMulti) return;
                onChange(currentIds.filter(v => String(v) !== String(id)));
            };
            const addMarketingSource = (val) => {
                const numeric = val === '' ? null : Number(val);
                if (numeric == null || isNaN(numeric)) { setMultiMarketingSourceTemp(''); return; }
                if (!currentIds.some(v => Number(v) === numeric)) {
                    onChange(isMulti ? [...currentIds, numeric] : numeric);
                }
                setMultiMarketingSourceTemp('');
            };
            return (
                <div className="user-select-wrapper">
                    <div className='select-wrapped' style={{ display: 'flex', gap: "5px", alignItems: 'center', justifyContent: "flex-end", flexDirection: 'row-reverse' }}>
                        <div className="user-select-toggle-row">
                            <button type="button" className={`action alt ${isMulti ? 'active' : ''}`} onClick={toggleMulti} title="Toggle multi Marketing Source OR filter">{isMulti ? 'Multi' : 'Single'}</button>
                        </div>
                        {isMulti ? (
                            <div className='filter-group' style={{ width: '100%' }}>
                                <div className='select-wrapper' style={{ width: '100%' }}>
                                    <select className='filter-select' style={{ width: '100%' }} value={multiMarketingSourceTemp} onChange={e => addMarketingSource(e.target.value)}>
                                        <option value=''>Add Marketing Source...</option>
                                        {options.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
                                    </select>
                                    <ChevronDown size={16} className="select-chevron" />
                                </div>
                            </div>
                        ) : (
                            <Dropdown options={JSON.stringify(options)} value={value} onChange={onChange} />
                        )}
                    </div>
                    {isMulti && currentIds.length > 0 && (
                        <div className="selected-users-chips">
                            {currentIds.map(id => {
                                const ms = marketingSources.find(m => Number(m.id) === Number(id));
                                return (
                                    <span key={id} className="chip">
                                        {(ms && ms.source_name) || id}
                                        <button type="button" className="chip-x" onClick={() => removeId(id)} aria-label="Remove marketing source">×</button>
                                    </span>
                                );
                            })}
                        </div>
                    )}
                </div>
            );
        }

        // User fields (First Primary / Created By / Team Member) with support for multi-user selection (array -> backend IN clause on '=')
        if (isTeamMemberCriteriaField(field) || field?.name === 'Created By' || field?.name === 'Assignees' || field?.name === 'Task Author' || field?.name === 'Team Member') {
            const isMulti = Array.isArray(value);
            const currentIds = isMulti ? value : (value ? [value] : []);
            const isMeMode = !isMulti && value === 'me';
            const isMeSelected = isMulti ? currentIds.includes('me') : value === 'me';
            
            const toggleMulti = () => {
                if (isMulti) {
                    // Collapse to single: keep first if exists
                    const first = currentIds[0] || null;
                    onChange(first);
                } else {
                    // Expand: seed with existing single id (if any)
                    onChange(currentIds);
                }
            };
            
            const toggleMe = () => {
                if (isMulti) {
                    // In multi mode, toggle "me" in the array
                    if (currentIds.includes('me')) {
                        onChange(currentIds.filter(id => id !== 'me'));
                    } else {
                        onChange([...currentIds, 'me']);
                    }
                } else {
                    // In single mode, toggle between "me" and empty
                    onChange(value === 'me' ? '' : 'me');
                }
            };
            
            const removeId = (id) => {
                if (!isMulti) return; // no-op in single mode
                onChange(currentIds.filter(v => String(v) !== String(id)));
            };
            const addTempUser = (val) => {
                const uid = val?.id || val?.user_id || val;
                if (!uid) return;
                
                // Only add if not already in the list
                if (!currentIds.some(v => String(v) === String(uid))) {
                    onChange(isMulti ? [...currentIds, uid] : uid);
                }
            };
            return (
                <div className="user-select-wrapper">
                    <div className='select-wrapped' style={{ display: 'flex', gap: "5px", alignItems: 'center', justifyContent: "flex-end", flexDirection: 'row-reverse' }}>
                        <div className="user-select-toggle-row">
                            <button 
                                type="button" 
                                className={`action ${isMeSelected ? 'tertiary' : 'quaternary'}`} 
                                style={{ borderColor: isMeSelected ? 'var(--fill)' : 'var(--border-color)' }}
                                onClick={toggleMe} 
                                title="Filter by current user"
                            >
                                Me
                            </button>
                            <button type="button" className={`action alt ${isMulti ? 'active' : ''}`} onClick={toggleMulti} title="Toggle multi-user OR filter">{isMulti ? 'Multi' : 'Single'}</button>
                        </div>
                        {!isMeMode && (isMulti ? (
                            <Contact
                                key={`multi-contact-${contactResetKey}`}
                                users={users}
                                selectedContact={null}
                                setSelectedContact={(val) => {
                                    addTempUser(val);
                                    // Force component reset by changing the key
                                    setContactResetKey(prev => prev + 1);
                                }}
                                disabled={false}
                            />
                        ) : (
                            <Contact
                                users={users}
                                selectedContact={value}
                                setSelectedContact={(val) => onChange(val?.id || val?.user_id || val)}
                                disabled={false}
                            />
                        ))}
                    </div>
                    {isMulti && currentIds.length > 0 && (
                        <div className="selected-users-chips">
                            {currentIds.map(id => {
                                if (id === 'me') {
                                    return (
                                        <span key="me" className="chip">
                                            Me (Current User)
                                            <button type="button" className="chip-x" onClick={() => removeId('me')} aria-label="Remove me">×</button>
                                        </span>
                                    );
                                }
                                const u = users.find(u => String(u.id) === String(id));
                                return (
                                    <span key={id} className="chip">
                                        {(u && (u.name || u.full_name)) || id}
                                        <button type="button" className="chip-x" onClick={() => removeId(id)} aria-label="Remove user">×</button>
                                    </span>
                                );
                            })}
                        </div>
                    )}
                </div>
            );
        }

        // Built-in, custom, and computed dates share the same input behavior.
        // Deadline type 20 keeps its separate due/done and status controls.
        if (isReportDateCriteriaField(field)) {
            if (operator === 'BETWEEN') {
                const betweenValues = Array.isArray(value) ? value : ['', ''];
                return (
                    <div className="between-inputs">
                        <DateInput 
                            value={String(betweenValues[0] || '')} 
                            onChange={(val) => onChange([val, betweenValues[1]])}
                            placeholder="Start date"
                        />
                        <span className="between-separator">and</span>
                        <DateInput 
                            value={String(betweenValues[1] || '')} 
                            onChange={(val) => onChange([betweenValues[0], val])}
                            placeholder="End date"
                        />
                    </div>
                );
            }
            if (operator === 'THIS_MONTH') {
                const now = new Date();
                const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
                const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                const startDate = firstDay.toISOString().split('T')[0];
                const endDate = lastDay.toISOString().split('T')[0];
                
                // Auto-set the value if not already set
                if (!Array.isArray(value) || value[0] !== startDate || value[1] !== endDate) {
                    onChange([startDate, endDate]);
                }
                
                return (
                    <div className="date-range-display" style={{ height: '36px', display: 'flex', alignItems: 'center' }}>
                        <span className="subtext">Current month: {startDate} to {endDate}</span>
                    </div>
                );
            }
            if (operator === 'THIS_YEAR') {
                const now = new Date();
                const startDate = `${now.getFullYear()}-01-01`;
                const endDate = `${now.getFullYear()}-12-31`;
                
                // Auto-set the value if not already set
                if (!Array.isArray(value) || value[0] !== startDate || value[1] !== endDate) {
                    onChange([startDate, endDate]);
                }
                
                return (
                    <div className="date-range-display" style={{ height: '36px', display: 'flex', alignItems: 'center' }}>
                        <span className="subtext">Current year: {startDate} to {endDate}</span>
                    </div>
                );
            }
            if (operator === 'WITHIN') {
                return <NumberInput type="number" value={String(value ?? '')} onChange={onChange} placeholder="Days" />;
            }
            if (operator === 'WITHIN_AFTER') {
                return <NumberInput type="number" value={String(value ?? '')} onChange={onChange} placeholder="Days after today" />;
            }
            if (operator === 'BEFORE/AFTER') {
                const pair = Array.isArray(value) ? value : [0,0];
                return (
                    <div className='before-after-inputs'>
                        <NumberInput type='number'
                            value={String(pair[0])}
                            onChange={(v) => {
                                const num = parseInt(v||'0',10); updateCondition(uid,'value',[num, pair[1]]);
                            }}
                            placeholder='Days before'
                        />
                        <span className='between-separator'>to</span>
                        <NumberInput type='number'
                            value={String(pair[1])}
                            onChange={(v) => {
                                const num = parseInt(v||'0',10); updateCondition(uid,'value',[pair[0], num]);
                            }}
                            placeholder='Days after'
                        />
                    </div>
                );
            }
            return <DateInput value={String(value || '')} onChange={onChange} />;
        }

        // Handle "between" operator for date fields
        if (operator === 'BETWEEN' && field?.field_id === 6) {
            const betweenValues = Array.isArray(value) ? value : ['', ''];
            return (
                <div className="between-inputs">
                    <DateInput 
                        value={String(betweenValues[0] || '')} 
                        onChange={(val) => onChange([val, betweenValues[1]])}
                        placeholder="Start date"
                    />
                    <span className="between-separator">and</span>
                    <DateInput 
                        value={String(betweenValues[1] || '')} 
                        onChange={(val) => onChange([betweenValues[0], val])}
                        placeholder="End date"
                    />
                </div>
            );
        }

        // Handle "this month" and "this year" operators for date fields
        if ((operator === 'THIS_MONTH' || operator === 'THIS_YEAR') && field?.field_id === 6) {
            const now = new Date();
            let startDate, endDate;
            
            if (operator === 'THIS_MONTH') {
                const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
                const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                startDate = firstDay.toISOString().split('T')[0];
                endDate = lastDay.toISOString().split('T')[0];
            } else {
                startDate = `${now.getFullYear()}-01-01`;
                endDate = `${now.getFullYear()}-12-31`;
            }
            
            // Auto-set the value if not already set
            if (!Array.isArray(value) || value[0] !== startDate || value[1] !== endDate) {
                onChange([startDate, endDate]);
            }
            
            return (
                <div className="date-range-display" style={{ height: '36px', display: 'flex', alignItems: 'center' }}>
                    <span className="subtext">
                        {operator === 'THIS_MONTH' ? 'Current month' : 'Current year'}: {startDate} to {endDate}
                    </span>
                </div>
            );
        }

        if (isContactNameCriteriaField(field) && isContactNameTextOperator(operator)) {
            return <Text type='text' value={String(value ?? '')} onChange={onChange} placeholder='Contact name' />;
        }

        if (field?.field_id === 20) {
            const isEquality = ['=','!='].includes(operator);
            
            if (isEquality) {
                const deadlineStatuses = [
                    ['','-- Select --'],
                    ['DONE','Done'],
                    ['DUE_TODAY','Due Today'],
                    ['MISSING_DUE','Missing Due Date'],
                    ['MISSING_DUE_OR_HIDDEN','Missing Due Date or Hidden Section'],
                    ['NOT_DONE_HAS_DUE','Not Done (But has a due date)'],
                    ['OVERDUE','Overdue'],
                    ['OVERDUE_OR_DUE_WITHIN_180','Overdue, or Due Within 180 Days'],
                    ['OVERDUE_OR_DUE_WITHIN_30','Overdue, or Due Within 30 Days'],
                    ['OVERDUE_OR_DUE_WITHIN_60','Overdue, or Due Within 60 Days'],
                    ['OVERDUE_OR_DUE_WITHIN_7','Overdue, or Due Within 7 Days'],
                    ['OVERDUE_OR_DUE_WITHIN_90','Overdue, or Due Within 90 Days']
                ];
                return <Dropdown options={JSON.stringify(deadlineStatuses)} value={value} onChange={onChange} />;
            }
            // Non-equality operators: status not applicable; allow due/done selection
            let part = deadline_part || 'due';
            if (part === 'status') { part = 'due'; }
            
            const deadlinePartSelect = (
                <select className='mini-select' value={part} onChange={e=> updateCondition(uid,'deadline_part', e.target.value)} style={{marginRight:4}}>
                    <option value='due'>Due Date</option>
                    <option value='done'>Done Date</option>
                </select>
            );
            if (operator === 'BETWEEN') {
                const betweenValues = Array.isArray(value) ? value : ['', ''];
                return <div style={{display:'flex',alignItems:'center'}}>{deadlinePartSelect}<div className='between-inputs'><DateInput value={String(betweenValues[0]||'')} onChange={(val)=> onChange([val, betweenValues[1]])} /><span className='between-separator'>and</span><DateInput value={String(betweenValues[1]||'')} onChange={(val)=> onChange([betweenValues[0], val])} /></div></div>;
            }
            if (operator === 'THIS_MONTH') {
                const now = new Date();
                const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
                const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                const startDate = firstDay.toISOString().split('T')[0];
                const endDate = lastDay.toISOString().split('T')[0];
                
                // Auto-set the value if not already set
                if (!Array.isArray(value) || value[0] !== startDate || value[1] !== endDate) {
                    onChange([startDate, endDate]);
                }
                
                return (
                    <div style={{display:'flex',alignItems:'center'}}>
                        {deadlinePartSelect}
                        <div className="date-range-display" style={{ height: '36px', display: 'flex', alignItems: 'center' }}>
                            <span className="subtext">Current month: {startDate} to {endDate}</span>
                        </div>
                    </div>
                );
            }
            if (operator === 'THIS_YEAR') {
                const now = new Date();
                const startDate = `${now.getFullYear()}-01-01`;
                const endDate = `${now.getFullYear()}-12-31`;
                
                // Auto-set the value if not already set
                if (!Array.isArray(value) || value[0] !== startDate || value[1] !== endDate) {
                    onChange([startDate, endDate]);
                }
                
                return (
                    <div style={{display:'flex',alignItems:'center'}}>
                        {deadlinePartSelect}
                        <div className="date-range-display" style={{ height: '36px', display: 'flex', alignItems: 'center' }}>
                            <span className="subtext">Current year: {startDate} to {endDate}</span>
                        </div>
                    </div>
                );
            }
            if (operator === 'WITHIN') {
                return <div style={{display:'flex',alignItems:'center'}}>{deadlinePartSelect}<NumberInput type='number' value={String(value||'')} onChange={(v)=> onChange(parseInt(v||'0',10))} placeholder='Days back' /></div>;
            }
            if (operator === 'WITHIN_AFTER') {
                return <div style={{display:'flex',alignItems:'center'}}>{deadlinePartSelect}<NumberInput type='number' value={String(value||'')} onChange={(v)=> onChange(parseInt(v||'0',10))} placeholder='Days after today' /></div>;
            }
            if (operator === 'BEFORE/AFTER') {
                const pair = Array.isArray(value) ? value : [0,0];
                return <div style={{display:'flex',alignItems:'center'}}>{deadlinePartSelect}<div className='before-after-inputs'><NumberInput type='number' value={String(pair[0])} onChange={(v)=> onChange([parseInt(v||'0',10), pair[1]])} /><span className='between-separator'>to</span><NumberInput type='number' value={String(pair[1])} onChange={(v)=> onChange([pair[0], parseInt(v||'0',10)])} /></div></div>;
            }
            return <div style={{display:'flex',alignItems:'center'}}>{deadlinePartSelect}<DateInput value={String(value||'')} onChange={onChange} /></div>;
        }

        switch (field?.field_id) {
            case 1:
                return <Text type='text' value={value} onChange={onChange} placeholder={field?.name} />;
            case 2:
                return <Text type='textarea' value={value} onChange={onChange} placeholder={field?.name} />;
            case 3:
                return <NumberInput type="currency" value={value} onChange={onChange} />;
            case 4:
                return <NumberInput type="percent" value={value} onChange={onChange} />;
            case 5:
                return <NumberInput type="number" value={value} onChange={onChange} />;
            case 6: // Date custom field
                if (operator === 'BETWEEN') {
                    const betweenValues = Array.isArray(value) ? value : ['', ''];
                    return (
                        <div className="between-inputs">
                            <DateInput 
                                value={String(betweenValues[0] || '')} 
                                onChange={(val) => onChange([val, betweenValues[1]])}
                                placeholder="Start date"
                            />
                            <span className="between-separator">and</span>
                            <DateInput 
                                value={String(betweenValues[1] || '')} 
                                onChange={(val) => onChange([betweenValues[0], val])}
                                placeholder="End date"
                            />
                        </div>
                    );
                }
                if (operator === 'THIS_MONTH') {
                    const now = new Date();
                    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
                    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                    const startDate = firstDay.toISOString().split('T')[0];
                    const endDate = lastDay.toISOString().split('T')[0];
                    
                    // Auto-set the value if not already set
                    if (!Array.isArray(value) || value[0] !== startDate || value[1] !== endDate) {
                        onChange([startDate, endDate]);
                    }
                    
                    return (
                        <div className="date-range-display" style={{ height: '36px', display: 'flex', alignItems: 'center' }}>
                            <span className="subtext">Current month: {startDate} to {endDate}</span>
                        </div>
                    );
                }
                if (operator === 'THIS_YEAR') {
                    const now = new Date();
                    const startDate = `${now.getFullYear()}-01-01`;
                    const endDate = `${now.getFullYear()}-12-31`;
                    
                    // Auto-set the value if not already set
                    if (!Array.isArray(value) || value[0] !== startDate || value[1] !== endDate) {
                        onChange([startDate, endDate]);
                    }
                    
                    return (
                        <div className="date-range-display" style={{ height: '36px', display: 'flex', alignItems: 'center' }}>
                            <span className="subtext">Current year: {startDate} to {endDate}</span>
                        </div>
                    );
                }
                if (operator === 'WITHIN') {
                    return <NumberInput type="number" value={String(value ?? '')} onChange={onChange} placeholder="Days" />;
                }
                if (operator === 'WITHIN_AFTER') {
                    return <NumberInput type="number" value={String(value ?? '')} onChange={onChange} placeholder="Days after today" />;
                }
                return <DateInput value={String(value || '')} onChange={onChange} />;
            case 7: // Time
                return <TimeInput value={value} onChange={onChange} />;
            case 8: // Person
                if (isContactNameCriteriaField(field) && isContactNameTextOperator(operator)) {
                    return <Text type='text' value={String(value ?? '')} onChange={onChange} placeholder='Contact name' />;
                }
                return (
                    <Contact
                        selectedContact={value}
                        setSelectedContact={(contact) => onChange(contact?.id || null)}
                    />
                );
            case 9: // Person List
                if (isContactNameCriteriaField(field) && isContactNameTextOperator(operator)) {
                    return <Text type='text' value={String(value ?? '')} onChange={onChange} placeholder='Contact name' />;
                }
                return (
                    <ContactList
                        value={Array.isArray(value) ? value : []}
                        onChange={(ids) => onChange(ids)}
                    />
                );
            case 10:
                return <Dropdown options={field.options || "[]"} value={value} onChange={onChange} />;
            case 11:
                return <MultiSelect options={field.options || "[]"} value={value} onChange={onChange} />;
            case 12:
                // Boolean: 0=Yes, 1=No, 2=Unknown - preserve exact numeric value
                const boolValue = value !== null && value !== undefined && value !== '' ? Number(value) : 0;
                return <Boolean options={field.options || []} value={boolValue} onChange={onChange} />;
            case 22:
                return <SearchSelect options={field.options || []} value={value} onChange={onChange} />;
            case 20:
                // Deadline status dropdown mapping to backend status codes
                const deadlineStatuses = [
                    ['','-- Select --'],
                    ['DONE','Done'],
                    ['DUE_TODAY','Due Today'],
                    ['MISSING_DUE','Missing Due Date'],
                    ['MISSING_DUE_OR_HIDDEN','Missing Due Date or Hidden Section'],
                    ['NOT_DONE_HAS_DUE','Not Done (But has a due date)'],
                    ['OVERDUE','Overdue'],
                    ['OVERDUE_OR_DUE_WITHIN_180','Overdue, or Due Within 180 Days'],
                    ['OVERDUE_OR_DUE_WITHIN_30','Overdue, or Due Within 30 Days'],
                    ['OVERDUE_OR_DUE_WITHIN_60','Overdue, or Due Within 60 Days'],
                    ['OVERDUE_OR_DUE_WITHIN_7','Overdue, or Due Within 7 Days'],
                    ['OVERDUE_OR_DUE_WITHIN_90','Overdue, or Due Within 90 Days']
                ];
                return <Dropdown options={JSON.stringify(deadlineStatuses)} value={value} onChange={onChange} />;
            default:
                return <Text type="text" value={value} onChange={onChange} placeholder={field?.name} />;
        }
    };

    const field = customFields && Array.isArray(customFields) ? customFields.find(f => f.id == field_id) : null;

    return (
        <div 
            ref={(node) => {
                rowRef.current = node;
                drag(drop(node));
            }}
            className={`criteria-child ${isDragging ? 'dragging' : ''}`}
            style={{ opacity: isDragging ? 0.5 : 1 }}
        >
            <div className='criteria-selectors'>
                <span className='criteria-name' style={{ textWrap: "nowrap" }}>{field?.name}</span>
                {(() => { 
                    const isGeneralDate = isReportDateCriteriaField(field);
                    
                    return (
                        <div className='filter-group' style={{ width: '50%' }}>
                            <div className='select-wrapper' style={{ width: '100%' }}>
                                <select 
                                    className='filter-select'
                                    style={{ width: '100%' }}
                                    value={operator} 
                                    onChange={(e) => {
                                        const nextOperator = e.target.value;
                                        updateCondition(uid, 'operator', nextOperator);
                                        if (isGeneralDate) {
                                            const nextValue = singleReportDateCriteriaValue(value, nextOperator);
                                            if (nextValue !== value) updateCondition(uid, 'value', nextValue);
                                        }
                                    }}
                                >
                                    <option value="=">Is</option>
                                    <option value="!=">Is Not</option>
                                    <option value=">">Greater Than</option>
                                    <option value="<">Less Than</option>
                                    <option value="LIKE">Contains</option>
                                    <option value="NOT LIKE">Does Not Contain</option>
                                    {Number(field?.add_item) === 1 && <option value="NO ENTRIES CONTAINS">No entries contains</option>}
                                    {(field?.field_id === 6 || isGeneralDate || field?.field_id === 20) && <option value="BETWEEN">Between</option>}
                                    {(field?.field_id === 6 || isGeneralDate || field?.field_id === 20) && <option value="THIS_MONTH">This Month</option>}
                                    {(field?.field_id === 6 || isGeneralDate || field?.field_id === 20) && <option value="THIS_YEAR">This Year</option>}
                                    {(field?.field_id === 6 || isGeneralDate || field?.field_id === 20) && <option value="WITHIN">Within (days)</option>}
                                    {(field?.field_id === 6 || isGeneralDate || field?.field_id === 20) && <option value="WITHIN_AFTER">Within (days after today)</option>}
                                    {(isGeneralDate || field?.field_id === 20) && <option value="BEFORE/AFTER">Before...After (days)</option>}
                                    <option value="IS BLANK">IS BLANK</option>
                                    <option value="IS NOT BLANK">IS NOT BLANK</option>
                                </select>
                                <ChevronDown size={16} className="select-chevron" />
                            </div>
                        </div>
                    )
                })()}

                {!['IS BLANK', 'IS NOT BLANK'].includes(operator) && (
                    <div className='form-group nm'>
                        {renderCriteriaInput(field, value, (val) => updateCondition(uid, 'value', val), operator, deadline_part)}
                    </div>
                )}
            </div>
            <div className='criteria-remove-group x'>
                {onRemove && <X size={16} onClick={onRemove}/>}
            </div>
        </div>
    );
};

export const ConditionGroup = ({ uid, logic = 'AND', conditions = [], onRemove, index, moveCondition, onAddGroup, onToggleLogic, parentIndex, nestingLevel = 0, customFields, updateCondition, phases, templates = [], users = [], caseTypes = [], marketingSources = [], isRootAndContainerDraggedOver = false, onRootInfoEnter, onRootInfoMove, onRootInfoLeave, moveConditionBetweenGroups, groupNumber }) => {
    const groupKey = `group-${parentIndex || 'root'}-${index || 0}`;
    const currentNestingLevel = nestingLevel || 0;
    const MAX_NESTING_LEVEL = 1;
    
    const isMaxNestingReached = currentNestingLevel >= MAX_NESTING_LEVEL;
    
    const [{ isOver }, drop] = useDrop(() => ({
        accept: [ItemTypes.FIELD, ItemTypes.CONDITION],
        drop: (item, monitor) => {
            if (monitor.didDrop()) return;
            
            if (item.field) {
                onAddGroup(uid, { field_id: item.field.id, operator: '!=', value: '' });
                return { handled: true };
            } else if (item.type === 'row') {
                if (typeof moveConditionBetweenGroups === 'function') {
                    const moveResult = moveConditionBetweenGroups(item.groupUid, item.uid, uid);
                    if (moveResult && typeof moveResult.movedIndex === 'number') {
                        item.index = moveResult.movedIndex;
                        item.groupUid = moveResult.targetGroupUid;
                        return { handled: true };
                    }
                }
            }
        },
        collect: (monitor) => ({
            isOver: monitor.isOver({ shallow: false })
        }),
    }), [uid, index, parentIndex, onAddGroup, moveConditionBetweenGroups]);
    
    const moveConditionWithinGroup = useCallback((fromIndex, toIndex) => {
        moveCondition(parentIndex, index, fromIndex, toIndex);
    }, [index, moveCondition, parentIndex]);
    
    const safeConditions = Array.isArray(conditions) ? conditions : [];
    let groupCounter = 0;
    const groupNumberByIndex = {};
    const groupNumberBase = 2;
    safeConditions.forEach((condition, idx) => {
        if (condition && condition.conditions) {
            groupCounter += 1;
            groupNumberByIndex[idx] = groupNumberBase + groupCounter - 1;
        }
    });
    
    // Apply can-drop styling if this is the root group and container is being dragged over
    const showCanDrop = isOver || (currentNestingLevel === 0 && isRootAndContainerDraggedOver);
    
    return (
        <div className={`criteria-parent ${showCanDrop ? 'can-drop' : ''}`} ref={drop} data-nesting-level={currentNestingLevel}>
            <div className='criteria-group-header'>
                <div className='criteria-group-name'>
                    {index === 0 || index === undefined ? 'Root Group' : `Group ${groupNumber || groupNumberBase}`}
                    {(index === 0 || index === undefined) && currentNestingLevel === 0 && (
                        <Info
                            size={16}
                            className='info-icon'
                            onMouseEnter={onRootInfoEnter}
                            onMouseMove={onRootInfoMove}
                            onMouseLeave={onRootInfoLeave}
                        />
                    )}
                </div>
                <button 
                    className={`logic-toggle ${logic === 'AND' ? 'and' : 'or-ico'}`} 
                    onClick={() => onToggleLogic(parentIndex, index)}
                >
                    {logic}
                </button>
                <div className='criteria-action-container'>
                    {currentNestingLevel === 0 && (
                        <div className='add-nested-group'>
                            <Plus 
                                size={16} 
                                onClick={() => onAddGroup(uid, { conditions: [], logic: 'AND' })} 
                                className='report-section-open'
                            />
                        </div>
                    )}
                    {onRemove && (
                        <div className='criteria-remove-group'>
                            <X size={16} onClick={() => onRemove(parentIndex, index)} />
                        </div>
                    )}
                </div>
            </div>
        
            {safeConditions.map((condition, condIndex) => (
                <React.Fragment key={condition?.uid ? `cond-${condition.uid}` : `${groupKey}-condition-${condIndex}`}>
                    {condition && condition.conditions ? (
                        <ConditionGroup
                            {...condition} 
                            index={condIndex} 
                            parentIndex={index !== undefined ? index : 'root'}
                            onRemove={onRemove} 
                            moveCondition={moveCondition}
                            onAddGroup={onAddGroup}
                            onToggleLogic={onToggleLogic}
                            nestingLevel={currentNestingLevel + 1}
                            customFields={customFields}
                            updateCondition={updateCondition}
                            phases={phases}
                            templates={templates}
                            users={users}
                            caseTypes={caseTypes}
                            marketingSources={marketingSources}
                            moveConditionBetweenGroups={moveConditionBetweenGroups}
                            groupNumber={groupNumberByIndex[condIndex]}
                            key={condition?.uid ? `group-${condition.uid}` : `nested-${groupKey}-${condIndex}`}
                        />
                    ) : (
                        <ConditionRow 
                            {...condition} 
                            index={condIndex}
                            parentIndex={parentIndex} 
                            groupIndex={index}
                            groupUid={uid}
                            onRemove={() => onRemove(index, condIndex)} 
                            moveCondition={moveConditionWithinGroup}
                            customFields={customFields}
                            updateCondition={updateCondition}
                            phases={phases}
                            templates={templates}
                            users={users}
                            caseTypes={caseTypes}
                            marketingSources={marketingSources}
                            key={condition?.uid ? `row-${condition.uid}` : `row-${groupKey}-${condIndex}`}
                        />
                    )}
                </React.Fragment>
            ))}
        
            {safeConditions.length === 0 && (
                <div className='empty-group-message'>
                    {isMaxNestingReached ? 
                        "Maximum nesting level reached" : 
                        "Drag fields here or click + to add a nested group"}
                </div>
            )}
        </div>
    );
};

export const ReportCriteria = ({ customFields, sections, selectedCriteria, setSelectedCriteria, loadingCustomFields }) => {
    const [selectedSelection, setSelectedSection] = useState(null);
    const [searchCriteria, setSearchCriteria] = useState(null);
    const [criteriaExpanded, setCriteriaExpanded] = useState(false);
    const [phases, setPhases] = useState([]);
    const [users, setUsers] = useState([]);
    const [caseTypes, setCaseTypes] = useState([]);
    const [marketingSources, setMarketingSources] = useState([]);
    const banishedFields = [13, 14, 15, 16, 18, 19, 21, 23]; // allow 20 (deadline)

    useEffect(() => {
        const fetchPhases = async () => {
            try {
                const response = await fetch('https://api.casedb.co/phases.php', { credentials: 'include' });
                const data = await response.json();
                if (data.success) setPhases(data.phases);
            } catch (error) {
                console.error('Error fetching phases:', error);
            }
        };
        const fetchUsers = async () => {
            try {
                const response = await fetch(`https://api.casedb.co/user.php?users=true&all=true&time=${Date.now()}`, { credentials: 'include' });
                const data = await response.json();
                if (data.users) setUsers(data.users);
            } catch (error) {
                console.error('Error fetching users:', error);
            }
        };
        fetchPhases();
        fetchUsers();
        // Optional: attempt dynamic fetch of case types if endpoint exists
        (async () => {
            try {
                const resp = await fetch('https://api.casedb.co/case_types.php');
                if (resp.ok) {
                    const data = await resp.json();
                    if (Array.isArray(data?.case_types)) {
                        setCaseTypes(data.case_types.filter(ct => Number(ct.id) !== 0));
                    }
                }
            } catch (e) { /* fallback to static */ }
        })();
        // Fetch marketing sources
        (async () => {
            try {
                const resp = await fetch('https://api.casedb.co/marketing_sources.php', { credentials: 'include' });
                if (resp.ok) {
                    const data = await resp.json();
                    if (Array.isArray(data?.marketing_sources)) {
                        setMarketingSources(data.marketing_sources);
                    }
                }
            } catch (e) { console.error('Error fetching marketing sources:', e); }
        })();
    }, []);

    const genUid = () => 'c_' + Math.random().toString(36).slice(2,11);
    const addCondition = (field_id) => {
        setSelectedCriteria(prev => ({
            ...prev,
            conditions: [...prev.conditions, { uid: genUid(), field_id, operator: '!=', value: '' }]
        }));
    };

    const updateCondition = (uid, key, newValue) => {
        setSelectedCriteria(prev => {
            const update = (conditions) => conditions.map(cond => {
                if (cond.uid === uid) {
                    return { ...cond, [key]: newValue };
                } else if (cond.conditions) {
                    return { ...cond, conditions: update(cond.conditions) };
                }
                return cond;
            });
            return { ...prev, conditions: update(prev.conditions) };
        });
    };

    // Ensure all existing conditions (including newly added) have a uid
    useEffect(() => {
        const addMissingUids = (conditions) => {
            let changed = false;
            const visit = (list) => {
                // Filter out null/undefined values and map over valid conditions
                return (list || []).filter(c => c != null).map(c => {
                    if (c.conditions) {
                        const nested = visit(c.conditions);
                        if (!c.uid) { changed = true; return { ...c, uid: genUid(), conditions: nested }; }
                        if (nested !== c.conditions) { changed = true; return { ...c, conditions: nested }; }
                        if (!c.uid) { changed = true; return { ...c, uid: genUid() }; }
                        return c;
                    } else {
                        if (!c.uid) { changed = true; return { ...c, uid: genUid() }; }
                        return c;
                    }
                });
            };
            const updated = visit(conditions || []);
            return { updated, changed };
        };
        setSelectedCriteria(prev => {
            // Only update if we actually need to add uids
            if (!prev || !prev.hasOwnProperty('conditions')) {
                return prev;
            }
            const { updated, changed } = addMissingUids(prev.conditions);
            return changed ? { ...prev, conditions: updated } : prev;
        });
    }, [setSelectedCriteria, selectedCriteria?.conditions]);

    const removeCondition = (groupIndex, condIndex) => {        
        setSelectedCriteria(prev => {
            const newCriteria = { ...prev };
            
            if (groupIndex === undefined || groupIndex === 'root') {
                const newConditions = [...newCriteria.conditions];
                newConditions.splice(condIndex, 1);
                newCriteria.conditions = newConditions;
                return newCriteria;
            }
            
            const deepCopy = JSON.parse(JSON.stringify(newCriteria));
            
            const findAndRemove = (conditions, targetParentIndex, targetChildIndex) => {
                const parentGroup = conditions[targetParentIndex];
                if (!parentGroup || !parentGroup.conditions) {
                    console.warn('Parent group not found or has no conditions:', targetParentIndex);
                    return conditions;
                }
                
                parentGroup.conditions.splice(targetChildIndex, 1);
                return conditions;
            };
            
            deepCopy.conditions = findAndRemove(deepCopy.conditions, parseInt(groupIndex), condIndex);
            return deepCopy;
        });
    };

    const addGroup = (targetGroupUid, newItem) => {
        setSelectedCriteria(prev => {
            const newCriteria = { ...prev };
            // attach uid if it's a condition object (has field_id) and missing uid
            let prepared = newItem;
            if (prepared && !prepared.uid) {
                prepared = { ...prepared, uid: genUid() };
            }
            
            if (!targetGroupUid) {
                const orderedRoot = [...newCriteria.conditions, prepared];
                const rows = orderedRoot.filter(c => !(c && c.conditions));
                const groups = orderedRoot.filter(c => c && c.conditions);
                newCriteria.conditions = [...rows, ...groups];
                return newCriteria;
            }
            
            const updateNestedGroup = (conditions) => {
                return conditions.map((condition) => {
                    if (condition && condition.uid === targetGroupUid && Array.isArray(condition.conditions)) {
                        return {
                            ...condition,
                            conditions: [...(condition.conditions || []), prepared]
                        };
                    } 
                    else if (condition.conditions) {
                        return {
                            ...condition,
                            conditions: updateNestedGroup(condition.conditions)
                        };
                    }
                    return condition;
                });
            };
            
            const updated = updateNestedGroup(newCriteria.conditions);
            // Ensure rows stay above nested groups
            const normalized = updated.map(cond => {
                if (cond && Array.isArray(cond.conditions)) {
                    const rows = cond.conditions.filter(c => !(c && c.conditions));
                    const groups = cond.conditions.filter(c => c && c.conditions);
                    return { ...cond, conditions: [...rows, ...groups] };
                }
                return cond;
            });
            const rootRows = normalized.filter(c => !(c && c.conditions));
            const rootGroups = normalized.filter(c => c && c.conditions);
            newCriteria.conditions = [...rootRows, ...rootGroups];
            return newCriteria;
        });
    };

    const toggleLogic = (parentIndex, groupIndex) => {
        setSelectedCriteria(prev => {
            const newCriteria = { ...prev };
            
            if (parentIndex === undefined && groupIndex === undefined) {
                newCriteria.logic = newCriteria.logic === 'AND' ? 'OR' : 'AND';
                return newCriteria;
            }
            
            const updateNestedLogic = (groups, path, targetIndex, currentLevel = 0) => {
                if (typeof path === 'string') {
                    path = path === 'root' ? [] : [parseInt(path)];
                } else if (typeof path === 'number') {
                    path = [path];
                }
              
                if (currentLevel >= path.length) {
                    return groups.map((group, idx) => {
                        if (idx === targetIndex && group.conditions !== undefined) {
                            return {
                            ...group,
                            logic: group.logic === 'AND' ? 'OR' : 'AND'
                            };
                        }
                        return group;
                    });
                }
              
                return groups.map((group, idx) => {
                    if (idx === path[currentLevel] && group.conditions) {
                        return {
                            ...group,
                            conditions: updateNestedLogic(group.conditions, path, targetIndex, currentLevel + 1)
                        };
                    }
                    return group;
                });
            };
            
            newCriteria.conditions = updateNestedLogic(newCriteria.conditions, parentIndex, groupIndex);
            return newCriteria;
        });
    };

    const moveCondition = (parentGroupIndex, groupIndex, fromIndex, toIndex) => {
        setSelectedCriteria(prev => {
            const newCriteria = { ...prev };
            
            if ((parentGroupIndex === undefined || parentGroupIndex === 'root') && groupIndex === undefined) {
                const newConditions = [...newCriteria.conditions];
                const [movedItem] = newConditions.splice(fromIndex, 1);
                newConditions.splice(toIndex, 0, movedItem);
                const rows = newConditions.filter(c => !(c && c.conditions));
                const groups = newConditions.filter(c => c && c.conditions);
                newCriteria.conditions = [...rows, ...groups];
                return newCriteria;
            }
            
            const updateNestedConditions = (conditions, path, targetIndex, fromIdx, toIdx, currentLevel = 0) => {
                if (typeof path === 'string') {
                    path = path === 'root' ? [] : [parseInt(path)];
                } else if (typeof path === 'number') {
                    path = [path];
                }
                
                if (currentLevel >= path.length) {
                    return conditions.map((cond, idx) => {
                        if (idx === targetIndex && cond.conditions) {
                            const newNestedConditions = [...cond.conditions];
                            const [movedItem] = newNestedConditions.splice(fromIdx, 1);
                            newNestedConditions.splice(toIdx, 0, movedItem);
                            const rows = newNestedConditions.filter(c => !(c && c.conditions));
                            const groups = newNestedConditions.filter(c => c && c.conditions);
                            return {
                                ...cond,
                                conditions: [...rows, ...groups]
                            };
                        }
                        return cond;
                    });
                }
              
                return conditions.map((cond, idx) => {
                    if (idx === path[currentLevel] && cond.conditions) {
                        return {
                            ...cond,
                            conditions: updateNestedConditions(cond.conditions, path, targetIndex, fromIdx, toIdx, currentLevel + 1)
                        };
                    }
                    return cond;
                });
            };
            
            const updated = updateNestedConditions(
                newCriteria.conditions, 
                parentGroupIndex, 
                groupIndex, 
                fromIndex, 
                toIndex
            );
            const rootRows = updated.filter(c => !(c && c.conditions));
            const rootGroups = updated.filter(c => c && c.conditions);
            newCriteria.conditions = [...rootRows, ...rootGroups];
            return newCriteria;
        });
    };

    const moveConditionBetweenGroups = (sourceGroupUid, sourceConditionUid, targetGroupUid) => {
        let result = null;
        setSelectedCriteria(prev => {
            const newCriteria = { ...prev };
            const rootConditions = Array.isArray(newCriteria.conditions) ? [...newCriteria.conditions] : [];

            const isSourceRoot = !sourceGroupUid;
            const isTargetRoot = !targetGroupUid;

            const findGroupByUid = (list, uid) => {
                for (const item of list || []) {
                    if (item && item.uid === uid && Array.isArray(item.conditions)) {
                        return item;
                    }
                    if (item && Array.isArray(item.conditions)) {
                        const found = findGroupByUid(item.conditions, uid);
                        if (found) return found;
                    }
                }
                return null;
            };

            const getGroupEntry = (groupUid) => {
                if (!groupUid) {
                    return { list: rootConditions, group: null };
                }
                const group = findGroupByUid(rootConditions, groupUid);
                if (group && Array.isArray(group.conditions)) {
                    return { list: group.conditions, group };
                }
                return { list: null, group: null };
            };

            const sourceEntry = getGroupEntry(sourceGroupUid);
            if (!sourceEntry.list) {
                return prev;
            }

            const sourceIndex = sourceEntry.list.findIndex(condition => condition?.uid === sourceConditionUid);
            if (sourceIndex === -1) {
                return prev;
            }

            const [movedItem] = sourceEntry.list.splice(sourceIndex, 1);
            if (!movedItem) {
                return prev;
            }

            const targetEntry = getGroupEntry(targetGroupUid);
            if (!targetEntry.list) {
                return prev;
            }

            const movedIndex = targetEntry.list.length;
            targetEntry.list.push(movedItem);

            if (!isSourceRoot && sourceEntry.group) {
                sourceEntry.group.conditions = sourceEntry.list
                    .filter(c => !(c && c.conditions))
                    .concat(sourceEntry.list.filter(c => c && c.conditions));
            }

            if (!isTargetRoot && targetEntry.group) {
                targetEntry.group.conditions = targetEntry.list
                    .filter(c => !(c && c.conditions))
                    .concat(targetEntry.list.filter(c => c && c.conditions));
            }

            const rootRows = rootConditions.filter(c => !(c && c.conditions));
            const rootGroups = rootConditions.filter(c => c && c.conditions);
            newCriteria.conditions = [...rootRows, ...rootGroups];
            result = { movedIndex, targetGroupUid: targetGroupUid || null };
            return newCriteria;
        });
        return result;
    };

    return (
        <DndProvider backend={HTML5Backend}>
            <ReportCriteriaContent
                customFields={customFields}
                sections={sections}
                selectedCriteria={selectedCriteria}
                setSelectedCriteria={setSelectedCriteria}
                searchCriteria={searchCriteria}
                setSearchCriteria={setSearchCriteria}
                selectedSelection={selectedSelection}
                setSelectedSection={setSelectedSection}
                addCondition={addCondition}
                updateCondition={updateCondition}
                removeCondition={removeCondition}
                addGroup={addGroup}
                toggleLogic={toggleLogic}
                moveCondition={moveCondition}
                moveConditionBetweenGroups={moveConditionBetweenGroups}
                phases={phases}
                users={users}
                caseTypes={caseTypes}
                marketingSources={marketingSources}
                banishedFields={banishedFields}
                loadingCustomFields={loadingCustomFields}
            />
        </DndProvider>
    );
};

// Inner component that uses useDrop (must be inside DndProvider)
const ReportCriteriaContent = ({
    customFields,
    sections,
    selectedCriteria,
    setSelectedCriteria,
    searchCriteria,
    setSearchCriteria,
    selectedSelection,
    setSelectedSection,
    addCondition,
    updateCondition,
    removeCondition,
    addGroup,
    toggleLogic,
    moveCondition,
    moveConditionBetweenGroups,
    phases,
    users,
    caseTypes,
    marketingSources,
    banishedFields,
    loadingCustomFields
}) => {
    const [showRootTooltip, setShowRootTooltip] = useState(false);
    const [rootTooltipPos, setRootTooltipPos] = useState({ x: 0, y: 0 });

    const handleRootMouseMove = (event) => {
        setRootTooltipPos({ x: event.clientX + 10, y: event.clientY });
    };

    // Memoize the options object to prevent infinite loops in SearchSelect
    const searchOptions = useMemo(() => {
        if (!customFields || !Array.isArray(customFields)) return {};
        
        return Object.fromEntries(customFields.map(field => {
            const fieldTypeLabels = {
                1: 'Text - Single Line',
                2: 'Text - Multi Line',
                3: 'Amount - Currency',
                4: 'Amount - Percent',
                5: 'Amount - Number',
                6: 'Date',
                7: 'Time',
                8: 'Person',
                9: 'Person List',
                10: 'Dropdown List',
                11: 'Multi-Select List',
                12: 'Yes/No',
                13: 'Subheader',
                14: 'Instructions',
                15: 'File',
                16: 'Multi File',
                17: 'Calculation',
                18: 'DocGen',
                19: 'Add Item',
                20: 'Deadline',
                21: 'Reference',
                22: 'Search Select',
                23: 'Action',
                24: 'ReportFusion',
                25: 'Rating'
            };
            const typeLabel = field.field_id ? fieldTypeLabels[field.field_id] : null;
            const displayName = typeLabel ? `${field.name} (${typeLabel})` : field.name;
            return [field.id, displayName];
        }));
    }, [customFields]);
    
    // Drop target for the entire criteria container (now safely inside DndProvider)
    const [{ isOverContainer }, dropContainer] = useDrop(() => ({
        accept: [ItemTypes.FIELD, ItemTypes.CONDITION],
        drop: (item, monitor) => {
            if (monitor.didDrop()) return;

            if (item.field) {
                addCondition(item.field.id);
                return { handled: true };
            }
            if (item.type === 'row' && typeof moveConditionBetweenGroups === 'function') {
                const moveResult = moveConditionBetweenGroups(item.groupUid, item.uid, null);
                if (moveResult && typeof moveResult.movedIndex === 'number') {
                    item.index = moveResult.movedIndex;
                    item.groupUid = null;
                    return { handled: true };
                }
            }
        },
        collect: (monitor) => ({
            isOverContainer: monitor.isOver({ shallow: false })
        })
    }), [addCondition, moveConditionBetweenGroups]);

    return (
        <div className='report-container'>
            <div className='criteria-picker'>
                {loadingCustomFields ? (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px',
                        gap: '8px',
                        minHeight: '100px',
                        width: '100%'
                    }}>
                        <Loader2 className='spinner' size={16} />
                        <span style={{ fontSize: '13px', color: '#666' }}>Loading available fields...</span>
                    </div>
                ) : (
                    <>
                        {/* Inject hardcoded Case Type field if not already supplied */}
                        {(() => {
                            const exists = customFields.some(f => f.id === 'leads.case_type_id');
                            if(!exists){
                                customFields.push({ id: 'leads.case_type_id', name: 'Case Type', field_id: null, section_id: JSON.stringify([0]) });
                            }
                            return null;
                        })()}
                        <SearchSelect
                                value={searchCriteria}
                                options={searchOptions}
                                placeholder='Select report criteria...'
                                onChange={(selected) => setSearchCriteria(selected)}
                            />
                        <div className='sections'>
                    {sections
                        .filter(section => {
                            if (searchCriteria) {
                                if (!customFields || !Array.isArray(customFields)) return false;
                                const selectedField = customFields.find(f => fieldIdsMatch(f.id, searchCriteria));
                                if (!selectedField) return false;

                                const sectionIds = Array.isArray(selectedField.section_id)
                                    ? selectedField.section_id
                                    : JSON.parse(selectedField.section_id || '[]');

                                return sectionIds.includes(Number(section.id));
                            } else {
                                return customFields.some(field => {
                                    const sectionIds = Array.isArray(field.section_id)
                                        ? field.section_id
                                        : JSON.parse(field.section_id || '[]');
                                    return sectionIds.includes(Number(section.id));
                                });
                            }
                        })
                        .sort((a, b) => {
                            const aOrder = Number(a.order_id);
                            const bOrder = Number(b.order_id);
                            
                            // If both negative, reverse order (larger negative first: -1, -2, -3)
                            if (aOrder < 0 && bOrder < 0) {
                                return bOrder - aOrder;
                            }
                            // If one negative and one positive, negative comes first
                            if (aOrder < 0) return -1;
                            if (bOrder < 0) return 1;
                            // Both positive, normal order
                            return aOrder - bOrder;
                        })
                        .slice(0, sections.length)
                        .map(section => (
                            <div
                                key={section.id}
                                className={`section-item ${selectedSelection === section.id ? 'active-section' : ''}`}
                                onClick={() => setSelectedSection(selectedSelection === section.id ? null : section.id)}
                            >
                                <span className='report-section-name'>
                                    {section.name}
                                    {selectedSelection !== section.id ? (
                                        <PlusIcon size={16} className='report-section-open' />
                                    ) : (
                                        <Minus size={16} className='report-section-open' />
                                    )}
                                </span>
                                {selectedSelection === section.id && (
                                <div className='report-fields'>
                                    {customFields
                                        .filter(field => {
                                            const sectionIds = Array.isArray(field.section_id)
                                                ? field.section_id
                                                : JSON.parse(field.section_id || '[]');

                                            const isInSection = sectionIds.includes(Number(section.id));
                                            const isNotBanished = !banishedFields.includes(Number(field?.field_id));

                                            if (searchCriteria) {
                                                return isInSection && isNotBanished && fieldIdsMatch(field.id, searchCriteria);
                                            }

                                            return isInSection && isNotBanished;
                                        })
                                        // removed slice limit to show all general fields including leads.*
                                        .map(field => (
                                            <DraggableField
                                                key={field.id}
                                                field={field}
                                                addCondition={addCondition}
                                            />
                                    ))}
                                </div>
                                )}
                            </div>
                        ))}
                        {/*!criteriaExpanded && (
                            <div className='section-item show-more' onClick={() => setCriteriaExpanded(true)}>
                                <span className='report-section-name'>Show more...</span>
                            </div>
                        )*/}
                        </div>
                    </>
                )}
            </div>
                <div className='criteria-container' ref={dropContainer} style={{ position: 'relative' }}>
                    {loadingCustomFields ? (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '20px',
                            gap: '8px',
                            minHeight: '100px',
                            width: '100%'
                        }}>
                            <Loader2 className='spinner' size={16} />
                            <span style={{ fontSize: '13px', color: '#666' }}>Loading available fields...</span>
                        </div>
                    ) : (
                        <>
                            <div className='criteria-actions'>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <button 
                                        className={`logic-toggle root ${selectedCriteria.logic === 'AND' ? 'and' : 'or-ico'}`} 
                                        onClick={() => toggleLogic()}
                                    >
                                        Root Group: {selectedCriteria.logic}
                                    </button>
                                </div>
                                <button 
                                    className='action alt'
                                    style={{ height: '36px' }}
                                    onClick={() => addGroup(null, { conditions: [], logic: 'AND' })}
                                >
                                    <Plus size={16} /> Add Group
                                </button>
                            </div>
                            <ConditionGroup 
                                {...selectedCriteria} 
                                onRemove={removeCondition} 
                                moveCondition={moveCondition}
                                onAddGroup={addGroup}
                                onToggleLogic={toggleLogic}
                                nestingLevel={0}
                                customFields={customFields}
                                updateCondition={updateCondition}
                                phases={phases}
                                users={users}
                                caseTypes={caseTypes}
                                marketingSources={marketingSources}
                                moveConditionBetweenGroups={moveConditionBetweenGroups}
                                key="root-group"
                                isRootAndContainerDraggedOver={isOverContainer}
                                onRootInfoEnter={() => setShowRootTooltip(true)}
                                onRootInfoMove={handleRootMouseMove}
                                onRootInfoLeave={() => setShowRootTooltip(false)}
                            />
                            <div className="criteria-info-box">
                                <strong>How criteria works</strong>
                                <div>
                                    <span>"AND"</span> means all rules must match. <span>"OR"</span> means any rule can match.
                                </div>
                                <div>
                                    Criteria run top-to-bottom, and you can drag rows to reorder them.
                                </div>
                                <div>
                                    Use nested groups to combine AND/OR blocks for more specific results.
                                </div>
                            </div>
                            {showRootTooltip && (
                                <div className="tooltip" style={{ top: `${rootTooltipPos.y}px`, left: `${rootTooltipPos.x}px` }}>
                                    Root group is the base level of criteria where you can add nested groups to return more specific data.
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
    );
};
